import React, { useCallback, useContext, useMemo } from 'react';
import { Chip } from '@crabs-ai/ds-react';
import { VideoItem } from '../../items/video/video-item-type';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { ItemSelectedForCropContext } from '../../context-provider';
import { getAssetFromItem } from '../../assets/utils';
import { getCropFromItem } from '../../utils/get-crop-from-item';
import { computeLayoutRect, type LayoutOption } from './compute-layout-rect';
import {
  useAssetFromItem,
  useDimensions,
  useWriteContext,
} from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';

interface LayoutControlProps {
  item: VideoItem;
}

const layoutOptions: LayoutOption[] = ['Fill', 'Fit'];

function LayoutControl({ item }: LayoutControlProps) {
  const { compositionWidth, compositionHeight } = useDimensions();
  const asset = useAssetFromItem(item);
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(item.id);

  const itemSelectedForCrop = useContext(ItemSelectedForCropContext);
  const itemIsBeingCropped = item.id === itemSelectedForCrop;

  // 에셋이 존재하면 원본 치수를 사용하고, 없으면 현재 아이템 치수를 사용한다
  const assetWidth = asset?.type === 'video' ? asset.width : item.width;
  const assetHeight = asset?.type === 'video' ? asset.height : item.height;

  // 크롭 모드에서는 원본 전체 기준(crop=null), 그 외에는 보이는(크롭된) 영역 기준.
  const layoutCrop = useMemo(
    () => (itemIsBeingCropped ? null : getCropFromItem(item)),
    [itemIsBeingCropped, item],
  );

  const layoutRects = useMemo(() => {
    if (assetWidth <= 0 || assetHeight <= 0) {
      return null;
    }

    // Fill/Fit 각각에 대한 목표 치수를 미리 계산해두고 재사용한다
    return {
      Fill: computeLayoutRect({
        layout: 'Fill',
        assetWidth,
        assetHeight,
        compositionWidth,
        compositionHeight,
        crop: layoutCrop,
      }),
      Fit: computeLayoutRect({
        layout: 'Fit',
        assetWidth,
        assetHeight,
        compositionWidth,
        compositionHeight,
        crop: layoutCrop,
      }),
    };
  }, [
    assetWidth,
    assetHeight,
    compositionWidth,
    compositionHeight,
    layoutCrop,
  ]);

  const activeLayout = useMemo<LayoutOption | null>(() => {
    if (!layoutRects) {
      return null;
    }

    // 아이템이 이미 Fill/Fit 중 어느 값에 가까운지 확인해 현재 선택 상태를 추정한다
    const tolerance = 8;
    const fitDiff =
      Math.abs(item.width - layoutRects.Fit.width) +
      Math.abs(item.height - layoutRects.Fit.height);
    const fillDiff =
      Math.abs(item.width - layoutRects.Fill.width) +
      Math.abs(item.height - layoutRects.Fill.height);

    if (fitDiff <= fillDiff) {
      return fitDiff <= tolerance ? 'Fit' : null;
    }
    return fillDiff <= tolerance ? 'Fill' : null;
  }, [item.height, item.width, layoutRects]);

  const handleLayoutChange = useCallback(
    (layout: LayoutOption) => {
      // 다중 선택이면 각 아이템의 원본 치수·크롭이 다르므로, 대상마다 자기 기준으로
      // 레이아웃 rect 를 계산해 적용한다(단일 아이템 rect 를 전부에 복사하지 않는다).
      setState({
        update: (state) => {
          const { assets } = state.undoableState;
          return changeItemsWithSelection(
            state,
            item.id,
            targetIds,
            (currentItem) => {
              if (currentItem.type !== 'video') {
                return currentItem;
              }

              const currentAsset = getAssetFromItem({
                item: currentItem,
                assets,
              });
              const itemAssetWidth =
                currentAsset?.type === 'video'
                  ? currentAsset.width
                  : currentItem.width;
              const itemAssetHeight =
                currentAsset?.type === 'video'
                  ? currentAsset.height
                  : currentItem.height;
              if (itemAssetWidth <= 0 || itemAssetHeight <= 0) {
                return currentItem;
              }

              // 크롭 모드인 아이템은 원본 전체 기준(crop=null), 그 외엔 보이는 영역 기준.
              const itemCrop =
                currentItem.id === itemSelectedForCrop
                  ? null
                  : getCropFromItem(currentItem);

              const rect = computeLayoutRect({
                layout,
                assetWidth: itemAssetWidth,
                assetHeight: itemAssetHeight,
                compositionWidth,
                compositionHeight,
                crop: itemCrop,
              });

              return {
                ...currentItem,
                width: rect.width,
                height: rect.height,
                left: rect.left,
                top: rect.top,
              };
            },
          );
        },
        commitToUndoStack: true,
      });
    },
    [
      item.id,
      targetIds,
      setState,
      itemSelectedForCrop,
      compositionWidth,
      compositionHeight,
    ],
  );

  return (
    <div className="flex h-full w-full flex-col gap-4xs">
      <div className="flex w-full flex-wrap items-center gap-2xs">
        {layoutOptions.map((layout) => (
          <Chip
            key={layout}
            style="filled"
            size="md"
            isShowLeadingIcon={false}
            labelNode={layout}
            selected={activeLayout === layout}
            onClick={() => handleLayoutChange(layout)}
          />
        ))}
      </div>
    </div>
  );
}

export default LayoutControl;
